/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import "./InputSlider.scss";
import { DataEncoding } from "../../services/types/gallus";
import { formatterWrapper } from "../../features/PlotlyFormatter";

interface InputSliderProps {
  /**
   * startingValue: the starting value of the input slider. If null, the input slider will be inactive and grayed out
   */
  startingValue: number | null;
  /**
   * leadingSymbol: symbol to the left of the input box.
   */
  leadingSymbol: string;
  /**
   * trailingSymbol: symbol to the right of the input box.
   */
  trailingSymbol: string;
  /**
   * label: label of the input
   */
  label: string | null;
  /**
   * showLabel: boolean value that determines if the label is shown or not
   */
  showLabel: boolean;
  /**
   * onValueChange: function that returns the new value of the input and slider, and the "identity" of this slider.
   * @param value
   * @param index
   * @returns
   */
  onValueChange: (value: number) => void;
  /**
   * labelVerticality: if true, the label is vertical. If False, it's horizontal.
   */
  labelVerticality: boolean;
  /**
   * labelTop: if true, the label is located at the top of the input. If False, it's located at the side.
   */
  labelTop: boolean;
}

interface InputSliderState {
  valueNumber: number | null;
  min: number;
  max: number;
  step: number;
}

/**
 * Component that displays a value in Input and Slider format
 */
export const InputSlider = (props: InputSliderProps) => {
  const [state, setState] = useState<InputSliderState>({
    valueNumber: 0,
    min: 0,
    max: 0,
    step: 0,
  });

  useEffect(() => {
    const adjustedStartingValue = props.startingValue ?? 0;

    const half = adjustedStartingValue * 0.5;
    const oneAndAHalf = adjustedStartingValue * 1.5;

    let min = 0;
    let max = 0;

    if (adjustedStartingValue === 0) {
      min = -100;
      max = 100;
    } else {
      min = half < oneAndAHalf ? half : oneAndAHalf;
      max = half > oneAndAHalf ? half : oneAndAHalf;
    }

    let step =
      adjustedStartingValue === 0 ? 0.5 : adjustedStartingValue * 0.015;

    setState({
      valueNumber: adjustedStartingValue,
      min: min,
      max: max,
      step: step < 0 ? step * -1 : step,
    });
  }, []);

  function handleChange(eventValue: number): void {
    setState({ ...state, valueNumber: eventValue });
    console.log(eventValue);
    props.onValueChange(eventValue);
  }

  return (
    <div
      className="input-slider-master"
      style={{ display: props.labelTop ? "block" : "flex" }}
    >
      <div
        className={
          "input-slider-label " +
          (props.labelVerticality ? "input-slider-label-vertical" : "")
        }
        style={{ display: props.showLabel ? "block" : "none" }}
      >
        {props.label}
      </div>
      <div className="input-slider-inputs">
        <div className="input-group input-group-sm input-slider-box">
          <div
            className="input-group-text"
            style={{
              visibility:
                props.leadingSymbol.length !== 0 ? "visible" : "hidden",
            }}
          >
            {props.leadingSymbol}
          </div>
          <input
            className="form-control projections-input-box-input input-slider-input-box-input"
            type="number"
            disabled={state.valueNumber === null}
            value={state.valueNumber ?? 0}
            onChange={(e) => handleChange(e.target.valueAsNumber)}
          />
          <div className="input-group-text">{props.trailingSymbol}</div>
        </div>
        <div className="input-slider-slider">
          <input
            disabled={state.valueNumber === null}
            value={state.valueNumber ?? 0}
            className="form-range"
            type="range"
            min={state.min}
            max={state.max}
            onChange={(e) => handleChange(e.target.valueAsNumber)}
            step={state.step}
          />
        </div>
      </div>
    </div>
  );
};
